import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import DocumentChatShell from '../shells/DocumentChatShell';
import AssistantBotRow from '../shells/AssistantBotRow';
import UserReplyRow from '../shells/UserReplyRow';
import { useDocumentChatNavigation } from '../shells/useDocumentChatNavigation';
import PdfPreviewActionButton from '../components/PdfPreviewActionButton';
import DocumentChatLayout from '../layout/DocumentChatLayout';
import { useWheelLocale } from '../hooks/useWheelLocale';
import { useStaggeredPreviewCta } from '../hooks/useStaggeredPreviewCta';
import { useChatThreadScroll } from '../hooks/useChatThreadScroll';
import { interpolateI18n } from '../utils/interpolateI18n';
import WorkerRegistryComposer from './WorkerRegistryComposer';
import { promptTextForStep } from './workerRegistryDisplay';
import { useWorkerRegistryChat } from './useWorkerRegistryChat';
import './WorkerRegistryChatFlow.css';

/** First assistant line after empty thread (~1s), then next block after a short gap (fill-in intro pattern). */
const FIRST_ASSISTANT_DELAY_MS = 1000;
const FOLLOW_ASSISTANT_GAP_MS = 650;

export interface WorkerRegistryChatFlowProps {
  formCode: string;
  /** Checklist row title (localized) — shown in greeting */
  documentTitle: string;
  onBack: () => void;
  /** Employee email — required for save/resume (omit only in isolated tests). */
  currentEmail?: string | null;
  /** End-of-flow PDF preview — parent owns navigation / fetch. */
  onOpenPdfPreview?: () => void;
}

const WorkerRegistryChatFlow: React.FC<WorkerRegistryChatFlowProps> = ({
  formCode,
  documentTitle,
  onBack,
  onOpenPdfPreview,
  currentEmail,
}) => {
  const { t } = useLanguage();
  const wheelLocale = useWheelLocale();
  const { exitToChecklist } = useDocumentChatNavigation(onBack);

  const flow = useWorkerRegistryChat(formCode, t, wheelLocale, currentEmail);

  const [revealPhase, setRevealPhase] = useState(0);

  const previewIntro = useMemo(
    () => interpolateI18n(t('submitDocuments.workerRegistryChat.previewIntro'), { documentTitle }),
    [t, documentTitle]
  );

  const threadReady = revealPhase >= 2;
  const greeting = `${t('submitDocuments.workerRegistryChat.greetingPrefix')} ${documentTitle}`;
  const previewEnabled = threadReady && flow.isComplete && !flow.loading && !flow.loadError;
  const { showIntro: showPreviewIntroBubble, showCta: showPreviewPdfCta } = useStaggeredPreviewCta(previewEnabled);

  useEffect(() => {
    if (flow.loading) return;

    setRevealPhase(0);
    if (flow.skipIntroDelay) {
      setRevealPhase(2);
      return;
    }
    const t1 = window.setTimeout(() => setRevealPhase(1), FIRST_ASSISTANT_DELAY_MS);
    const t2 = window.setTimeout(
      () => setRevealPhase(2),
      FIRST_ASSISTANT_DELAY_MS + FOLLOW_ASSISTANT_GAP_MS
    );
    return () => {
      window.clearTimeout(t1);
      window.clearTimeout(t2);
    };
  }, [formCode, flow.skipIntroDelay, flow.loading]);

  useChatThreadScroll(flow.threadEndRef, [
    revealPhase,
    flow.committed.length,
    flow.stepIndex,
    flow.editingCommitIndex,
    flow.loading,
    flow.loadError,
    flow.isComplete,
    flow.assistantQuestionVisible,
    showPreviewIntroBubble,
    showPreviewPdfCta,
  ]);

  return (
    <DocumentChatShell onBack={exitToChecklist} cancelLabel={t('submitDocuments.chat.cancelProcedure')}>
      <DocumentChatLayout
        thread={
          <>
            {revealPhase >= 1 && (
              <AssistantBotRow animateIn>{greeting}</AssistantBotRow>
            )}

            {threadReady && flow.loading && (
              <AssistantBotRow animateIn>{t('submitDocuments.workerRegistryChat.loading')}</AssistantBotRow>
            )}

            {threadReady && flow.loadError && !flow.loading && (
              <AssistantBotRow variant="error" animateIn>
                {t('submitDocuments.workerRegistryChat.loadError')}
              </AssistantBotRow>
            )}

            {threadReady && !flow.loading && !flow.loadError && flow.steps.length > 0 && (
              <>
                {flow.committed.map((c, i) => (
                  <React.Fragment key={`${c.stepId}-${i}`}>
                    <AssistantBotRow animateIn>{promptTextForStep(flow.steps[c.stepIndex], t)}</AssistantBotRow>
                    <UserReplyRow
                      displayText={c.displayText}
                      showEdit={flow.showEditForCommitIndex(i)}
                      onEdit={() => flow.jumpToEditCommitIndex(i)}
                      editLabel={t('submitDocuments.fillFormsChecklist.edit')}
                      showUndo={flow.showUndoForCommitIndex(i)}
                      onUndo={flow.undo}
                      undoLabel={t('submitDocuments.chat.undo')}
                    />
                  </React.Fragment>
                ))}

                {!flow.isComplete &&
                  flow.editingCommitIndex === null &&
                  flow.currentStep &&
                  flow.assistantQuestionVisible && (
                  <AssistantBotRow animateIn>{promptTextForStep(flow.currentStep, t)}</AssistantBotRow>
                )}

                {flow.isComplete && (
                  <AssistantBotRow animateIn>{t('submitDocuments.workerRegistryChat.allDone')}</AssistantBotRow>
                )}

                {flow.isComplete && showPreviewIntroBubble && (
                  <AssistantBotRow animateIn>{previewIntro}</AssistantBotRow>
                )}
              </>
            )}

            <div ref={flow.threadEndRef} aria-hidden />
          </>
        }
        composer={
          <>
            {threadReady && flow.isComplete && showPreviewPdfCta && onOpenPdfPreview && (
              <PdfPreviewActionButton
                label={t('submitDocuments.workerRegistryChat.previewPdf')}
                onClick={() => onOpenPdfPreview()}
              />
            )}

            {threadReady && !flow.loading && !flow.loadError && flow.currentStep && (
              <WorkerRegistryComposer
                step={flow.currentStep}
                value={flow.currentValue}
                onChange={(fieldName, v) => flow.setField(fieldName, v)}
                onSubmit={() => flow.advance()}
                canSubmit={flow.canSubmitCurrent}
                wheelLocale={wheelLocale}
                t={t}
                sendAriaLabel={t('submitDocuments.profileName.send')}
                hideDuplicateThreadPrompt
              />
            )}
          </>
        }
      />
    </DocumentChatShell>
  );
};

export default WorkerRegistryChatFlow;
